/**
 * Metric definitions shared by the web app and the Worker. Adding a metric is one
 * entry here plus locale strings — no schema change and no new route.
 */

/** Physical quantity of a metric field. Values are always stored in SI units. */
export type Quantity = 'mass' | 'length' | 'distance' | 'duration'

export type ExerciseMetricId = 'run' | 'swim' | 'cycle' | 'row'

export type MetricId = ExerciseMetricId | 'sleep'

export type MetricField = {
   key: string
   quantity: Quantity
   required: boolean
}

export type DerivedValueFn = (values: Record<string, number>) => number | null

export type MetricDefinition = {
   id: MetricId
   labelKey: string
   fields: readonly MetricField[]
   /** Values computed from the stored fields (e.g. pace from distance and duration). */
   derived: Record<string, DerivedValueFn>
   /** Field or derived key the timeline plots. */
   primary: string
   /** Field the distance chips are built from, snapped to `snapTo` SI units. */
   groupBy?: { field: string; snapTo: number }
   entriesPerDay: 'one' | 'many'
   charts: ReadonlyArray<'line' | 'bar'>
   /** Field summed per bucket in the bar chart. */
   barField?: string
   /** Whether a smaller primary value is the better one (true for pace). */
   lowerIsBetter: boolean
}

const METRES_PER_KM = 1000
const SECONDS_PER_HOUR = 3600
const DISTANCE_SNAP_M = 500
/** Runs slower than this (km/h) earn no effort — a brisk walk, not a run. */
const RUN_EFFORT_MIN_SPEED_KMH = 6
/** The effort multiplier doubles every this many km/h above the minimum speed. */
const RUN_EFFORT_DOUBLING_SPEED_KMH = 4

/**
 * Pace in seconds per kilometre, or null when the inputs are not positive.
 *
 * @param values - The entry values (`distance` in m, `duration` in s).
 * @returns Seconds per km, or null.
 */
function paceSecondsPerKm(values: Record<string, number>): number | null {
   const distance = values.distance ?? 0
   const duration = values.duration ?? 0
   if (distance <= 0 || duration <= 0) {
      return null
   }
   return duration / (distance / METRES_PER_KM)
}

/**
 * Speed in km/h, or null when the inputs are not positive.
 *
 * @param values - The entry values (`distance` in m, `duration` in s).
 * @returns Kilometres per hour, or null.
 */
function speedKmPerHour(values: Record<string, number>): number | null {
   const distance = values.distance ?? 0
   const duration = values.duration ?? 0
   if (distance <= 0 || duration <= 0) {
      return null
   }
   return distance / METRES_PER_KM / (duration / SECONDS_PER_HOUR)
}

/**
 * Run effort: distance (km) scaled by a pace multiplier that grows exponentially
 * with speed — `2^((speed − 6) / 4)`, so 6 km/h → 1×, 10 km/h → 2×, 14 km/h → 4×,
 * 18 km/h → 8×. Below 6 km/h the multiplier is 0 (2 km at 10 km/h → 4 effort).
 *
 * @param values - The entry values (`distance` in m, `duration` in s).
 * @returns The effort score, or null when the inputs are not positive.
 */
export function computeRunEffort(values: Record<string, number>): number | null {
   const speed = speedKmPerHour(values)
   if (speed === null) {
      return null
   }
   if (speed < RUN_EFFORT_MIN_SPEED_KMH) {
      return 0
   }
   const multiplier =
      2 ** ((speed - RUN_EFFORT_MIN_SPEED_KMH) / RUN_EFFORT_DOUBLING_SPEED_KMH)
   return multiplier * ((values.distance ?? 0) / METRES_PER_KM)
}

/**
 * Builds the definition shared by every distance-over-time exercise metric.
 *
 * @param id - The metric id.
 * @param labelKey - The translation key for the metric name.
 * @returns The metric definition.
 */
function distanceDurationMetric(
   id: ExerciseMetricId,
   labelKey: string,
): MetricDefinition {
   return {
      id,
      labelKey,
      fields: [
         { key: 'distance', quantity: 'distance', required: true },
         { key: 'duration', quantity: 'duration', required: true },
      ],
      derived: { pace: paceSecondsPerKm, speed: speedKmPerHour },
      primary: 'pace',
      groupBy: { field: 'distance', snapTo: DISTANCE_SNAP_M },
      entriesPerDay: 'many',
      charts: ['line', 'bar'],
      barField: 'distance',
      lowerIsBetter: true,
   }
}

const runMetric = distanceDurationMetric('run', 'Run')

/**
 * Sleep is timed like a fast: the entry is created when the sleep starts (`timestamp`)
 * and `duration` is filled in when it ends, so an entry without one is still running.
 */
const sleepMetric: MetricDefinition = {
   id: 'sleep',
   labelKey: 'Sleep',
   fields: [{ key: 'duration', quantity: 'duration', required: false }],
   derived: {},
   primary: 'duration',
   entriesPerDay: 'one',
   charts: ['bar'],
   barField: 'duration',
   lowerIsBetter: false,
}

export const METRIC_REGISTRY: Readonly<Record<MetricId, MetricDefinition>> = {
   run: { ...runMetric, derived: { ...runMetric.derived, effort: computeRunEffort } },
   swim: distanceDurationMetric('swim', 'Swim'),
   cycle: distanceDurationMetric('cycle', 'Cycle'),
   row: distanceDurationMetric('row', 'Row'),
   sleep: sleepMetric,
}

export const METRIC_IDS: readonly MetricId[] = Object.keys(METRIC_REGISTRY) as MetricId[]

/** The distance-over-time metrics: everything except sleep. */
export const EXERCISE_METRIC_IDS: readonly ExerciseMetricId[] = [
   'run',
   'swim',
   'cycle',
   'row',
]

/**
 * Returns whether a value is an exercise metric id.
 *
 * @param value - The candidate id.
 * @returns True for run, swim, cycle, or row.
 */
export function isExerciseMetricId(value: unknown): value is ExerciseMetricId {
   return EXERCISE_METRIC_IDS.includes(value as ExerciseMetricId)
}

/**
 * Returns whether an entry is a sleep that has started but not ended yet.
 *
 * @param entry - The entry.
 * @returns True for a live sleep entry without a duration.
 */
export function isSleepInProgress(entry: {
   metricId: MetricId
   values: Record<string, number>
   deletedAt: string | null
}): boolean {
   return (
      entry.metricId === 'sleep' &&
      entry.deletedAt === null &&
      entry.values.duration === undefined
   )
}

/**
 * Returns whether a value is a registered metric id.
 *
 * @param value - The candidate id.
 * @returns True for a known metric id.
 */
export function isMetricId(value: unknown): value is MetricId {
   return typeof value === 'string' && Object.hasOwn(METRIC_REGISTRY, value)
}

/**
 * Looks up a metric definition.
 *
 * @param id - The candidate metric id.
 * @returns The definition, or null when unknown.
 */
export function getMetricDefinition(id: unknown): MetricDefinition | null {
   return isMetricId(id) ? METRIC_REGISTRY[id] : null
}

/**
 * Computes every derived value of a metric for an entry's stored values.
 *
 * @param definition - The metric definition.
 * @param values - The stored field values in SI units.
 * @returns Derived values keyed by name (null when not computable).
 */
export function computeDerivedValues(
   definition: MetricDefinition,
   values: Record<string, number>,
): Record<string, number | null> {
   return Object.fromEntries(
      Object.entries(definition.derived).map(([key, compute]) => [key, compute(values)]),
   )
}
